/*
  Project page frame (UI overhaul wave 2, screen 04 ideas 1, 2 and 10) — shared by all 8 project tabs.

  - ProjectHeader       — one compact card instead of the 27-row "Project Details" wall: name + system +
                          status + handover warning, a client / location / Waze / contractor / consultant line,
                          six key facts, and the rest behind "All project details". "Edit details" opens the
                          drawer. On phones (compact) it is a name row, chips and three small numbers.
  - ProjectDetailsList  — the full details (payment terms, OneDrive folder, notes, tags, device-address rule).
  - ProjectTabsBar      — the tab row with counts, the Files attention dot and the save state on the right.
  - TabContentSkeleton / ProjectPageSkeleton — shimmer while a tab's records or the project load.

  Usage (ProjectDetailPage.js):
    <ProjectHeader project={project} facts={headerFacts} onEditDetails={…} canEditDetails menuItems={[…]}
      canViewHistory onOpenHistory={…} details={<ProjectDetailsList … />} compact={isPhone} />
    <Tabs value={tab} onValueChange={changeTab} activationMode="manual">
      <ProjectTabsBar tabs={visibleTabs} counts={tabCounts} filesWarn={why} dirtyCount={n} phone={isPhone} />
      <TabsContent …/>
    </Tabs>
*/
import { useId, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight, Building2, ChevronDown, ChevronLeft, Ellipsis, HardHat, History, MapPin, Navigation, SquarePen, UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { KeyFacts } from '@/components/ui/key-facts';
import { ProgressRing } from '@/components/ui/progress-bar';
import { Avatar } from '@/components/ui/avatar-initials';
import { TabsBar, SaveStatus } from '@/components/ui/tabs-bar';
import { SystemChip } from '@/components/ui/system-chip';
import { cn } from '@/lib/utils';
import { formatDate, formatNumber } from '@/lib/format';
import { ActionMenu, Panel, useElementWidth } from './ProjectUi';

const whole = (value) => formatNumber(value, { decimals: 0 });
const plural = (count, word, many = `${word}s`) => `${count} ${count === 1 ? word : many}`;

export const PROJECT_TABS = [
  { value: 'stages', label: 'Stages' },
  { value: 'material', label: 'Material' },
  { value: 'files', label: 'Files' },
  { value: 'invoicing', label: 'Invoicing' },
  { value: 'structure', label: 'Project Structure' },
  { value: 'execution', label: 'Execution' },
  { value: 'timeline', label: 'Timeline' },
  { value: 'variations', label: 'Variations' },
];

function HandoverPill({ handover, compact }) {
  if (!handover?.endDate) return null;
  if (handover.overdue && !handover.onHold) {
    return (
      <StatusPill
        tone="red"
        label={compact ? `Overdue ${handover.days} d` : `Handover overdue · ${handover.days} d`}
        title={`Handover date ${formatDate(handover.endDate)} passed ${plural(handover.days, 'day')} ago`}
        data-testid="project-handover-pill"
      />
    );
  }
  return null;
}

// "Name · Role" stays one unit: it is cut with … when narrow, never wrapped so the role hangs alone.
const memberText = (member) => `${member.name} · ${member.role}`;
// One meta part that never breaks inside ("208,577 received"); the line may wrap between parts.
const Nowrap = ({ children }) => <span className="whitespace-nowrap">{children}</span>;

function TeamFact({ team }) {
  if (!team.length) return <span className="text-[13px] font-semibold text-ink-3">No engineer assigned</span>;
  const [first] = team;
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="flex shrink-0">
        {team.map((member, index) => (
          <Avatar key={`${member.role}-${member.name}`} name={member.name} size={26} className={cn('ring-2 ring-[color:var(--surface)]', index > 0 && '-ml-1.5')} />
        ))}
      </span>
      <span className="min-w-0 truncate text-[13px] font-semibold leading-snug text-ink" title={memberText(first)}>
        {memberText(first)}
      </span>
    </span>
  );
}

function handoverMeta(handover) {
  if (!handover?.endDate) return handover?.startDate ? `Started ${formatDate(handover.startDate)}` : 'Set a handover date';
  const start = handover.startDate ? `started ${formatDate(handover.startDate)}` : 'start not set';
  if (handover.closed) return 'Project completed';
  // the start sits on its own line (as approved): no " · " left hanging at a line end
  const withStart = (lead) => (
    <>
      <span className="block">{lead}</span>
      <span className="block">{start}</span>
    </>
  );
  if (handover.onHold) return withStart('On hold');
  if (handover.overdue) return withStart(<b className="font-bold text-status-red-ink">{plural(handover.days, 'day')} overdue</b>);
  const left = Math.abs(handover.days || 0);
  return withStart(left === 0 ? 'Due today' : `in ${plural(left, 'day')}`);
}

/** The six key facts as KeyFacts items (roles that may not see money get four). */
export function buildKeyFactItems(facts) {
  const { contract, invoiced, stages, execution, handover, team } = facts;
  const items = [];
  if (contract) {
    items.push({
      key: 'contract',
      label: 'Contract value',
      prefix: 'AED',
      value: <Nowrap>{whole(contract.total)}</Nowrap>,
      meta: contract.variations > 0
        ? <><Nowrap>{whole(contract.base)} base</Nowrap> + <Nowrap>{whole(contract.variations)} VOs</Nowrap></>
        : <><Nowrap>Base scope</Nowrap> · <Nowrap>no approved VOs</Nowrap></>,
      testId: 'fact-contract',
    });
  }
  if (invoiced) {
    items.push({
      key: 'invoiced',
      label: 'Invoiced',
      prefix: 'AED',
      value: <Nowrap>{whole(invoiced.invoiced)}</Nowrap>,
      suffix: `${invoiced.percent}%`,
      progress: invoiced.percent,
      progressTone: 'lime',
      progressLabel: 'Invoiced of the contract value',
      meta: invoiced.invoiced <= 0
        ? 'Nothing invoiced yet'
        : (invoiced.pending > 0
          ? <><Nowrap>{whole(invoiced.received)} received</Nowrap> · <Nowrap>{whole(invoiced.pending)} pending</Nowrap></>
          : <><Nowrap>of contract</Nowrap> · <Nowrap>all received</Nowrap></>),
      testId: 'fact-invoiced',
    });
  }
  items.push({
    key: 'stages',
    label: 'Stages',
    ring: { value: stages?.percent || 0, label: 'Stages approved', size: 38 },
    value: stages?.total ? <><Nowrap>{stages.approved} of {stages.total}</Nowrap> approved</> : 'No stages',
    meta: stages?.next ? `Next: ${stages.next.name}` : (stages?.total ? 'All stages approved' : ''),
    testId: 'fact-stages',
  });
  const execPercent = execution?.percent;
  items.push({
    key: 'execution',
    label: 'Execution',
    ring: { value: execPercent || 0, tone: 'lime', size: 38, label: 'Execution tasks done', centerLabel: execPercent === null || execPercent === undefined ? '—' : `${execPercent}%` },
    value: execution?.counted ? <><Nowrap>{execution.done} of {execution.counted}</Nowrap> done</> : 'No tasks yet',
    meta: execution?.notRequired ? `${execution.notRequired} not required` : (execution?.counted ? `${execution.open} open` : ''),
    testId: 'fact-execution',
  });
  items.push({
    key: 'handover',
    label: 'Handover',
    value: handover?.endDate ? <Nowrap>{formatDate(handover.endDate)}</Nowrap> : 'Not set',
    valueTone: handover?.endDate ? undefined : undefined,
    size: 'md',
    meta: handoverMeta(handover),
    testId: 'fact-handover',
  });
  items.push({
    key: 'team',
    label: 'Team',
    content: <TeamFact team={team || []} />,
    meta: team?.[1] ? <span className="block truncate" title={memberText(team[1])}>{memberText(team[1])}</span> : '',
    testId: 'fact-team',
  });
  return items;
}

const FACT_COLUMNS = {
  // Stages / Execution carry a ring + a short sentence, Team two avatars + a name: they get the extra width.
  6: '0.95fr 1.1fr 1.2fr 1.12fr 0.88fr 1.35fr',
  5: '1fr 1.25fr 1.1fr 0.95fr 1.25fr',
  4: '1.25fr 1.1fr 1fr 1.25fr',
};
// One row of six facts needs ~165px a cell. KeyFacts switches to its one-row template at the lg viewport
// (1024px), but with the 248px sidebar the card is far narrower there — so the header measures its own width
// and keeps three columns (two rows) until the facts really fit in one row.
const ONE_ROW_MIN_WIDTH = { 6: 1060, 5: 880, 4: 700 };
const THREE_COLUMNS = 'repeat(3, minmax(0, 1fr))';

function InfoItem({ icon: Icon, children, title }) {
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1.5" title={title}>
      <Icon className="h-3.5 w-3.5 shrink-0 text-ink-4" aria-hidden="true" />
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}

function CompactFact({ label, value, ring, testId }) {
  return (
    <div className="min-w-0 rounded-[12px] border border-line bg-surface px-3 py-2 shadow-card" data-testid={testId}>
      <div className="text-[11.5px] font-semibold text-ink-3">{label}</div>
      <div className="mt-0.5 flex items-center gap-2">
        {ring ? <ProgressRing value={ring.value} tone={ring.tone} size={22} thickness={3} centerLabel="" label={ring.label} /> : null}
        <span className="font-heading text-[17px] font-extrabold tabular-nums text-ink">{value}</span>
      </div>
    </div>
  );
}

export function ProjectHeader({
  project,
  facts,
  compact = false,
  canViewHistory = false,
  onOpenHistory,
  canEditDetails = false,
  onEditDetails,
  menuItems = [],
  details,
  detailsOpen = false,
  onDetailsOpenChange,
  backTo,
  canViewFinance = true,
  className,
}) {
  const detailsId = useId();
  const factsRef = useRef(null);
  const factsWidth = useElementWidth(factsRef);
  const scope = Array.isArray(project?.scope) ? project.scope.filter(Boolean) : [];
  const items = buildKeyFactItems(facts);
  const oneRow = factsWidth === null || factsWidth >= (ONE_ROW_MIN_WIDTH[items.length] || 0);
  const factColumns = oneRow ? (FACT_COLUMNS[items.length] || undefined) : THREE_COLUMNS;
  const disclosure = (
    <div className={cn('flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12.5px]', compact ? 'mt-3' : 'mt-3.5')}>
      <button
        type="button"
        aria-expanded={detailsOpen}
        aria-controls={detailsId}
        onClick={() => onDetailsOpenChange?.(!detailsOpen)}
        className="inline-flex items-center gap-1 rounded font-semibold text-[color:var(--brand-ink)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid="project-details-toggle"
      >
        All project details
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', detailsOpen && 'rotate-180')} aria-hidden="true" />
      </button>
      {!compact ? <span className="text-ink-3">{canViewFinance ? 'Payment terms, OneDrive folder' : 'OneDrive folder'}, notes, tags, device-address rule</span> : null}
    </div>
  );
  const detailsBody = detailsOpen ? (
    <div id={detailsId} className="mt-3 border-t border-line-2 pt-3" data-testid="project-details-list">{details}</div>
  ) : <div id={detailsId} hidden />;

  if (compact) {
    const invoicedFact = facts.invoiced
      ? { label: 'Invoiced', value: `${facts.invoiced.percent}%` }
      : { label: 'Handover', value: facts.handover?.endDate ? formatDate(facts.handover.endDate, { year: 'never' }) : '—' };
    const execPercent = facts.execution?.percent;
    return (
      <header className={cn('min-w-0', className)} data-testid="project-header">
        <div className="flex items-start justify-between gap-2">
          {backTo ? (
            // a router Link: the page's unsaved-changes guard asks before leaving, as for any in-app link
            <Button asChild variant="ghost" size="icon" className="-ml-2 shrink-0">
              <Link to={backTo} aria-label="Back to projects" data-testid="project-back">
                <ChevronLeft aria-hidden="true" />
              </Link>
            </Button>
          ) : null}
          <h1 className="min-w-0 flex-1 font-heading text-[22px] font-black leading-tight tracking-[-0.02em] text-ink [overflow-wrap:anywhere]">{project.name}</h1>
          <ActionMenu
            label="Project actions"
            trigger={(
              <Button type="button" variant="ghost" size="icon" aria-label="Project actions" data-testid="project-header-menu">
                <Ellipsis aria-hidden="true" />
              </Button>
            )}
            items={[
              canEditDetails && { label: 'Edit details', icon: SquarePen, onSelect: onEditDetails },
              canViewHistory && { label: 'History', icon: History, onSelect: onOpenHistory },
              ...menuItems,
            ]}
          />
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <SystemChip systemType={project.system_type} />
          {project.status ? <StatusPill status={project.status} domain="project" /> : null}
          <HandoverPill handover={facts.handover} compact />
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <CompactFact label="Stages" value={facts.stages?.total ? `${facts.stages.approved}/${facts.stages.total}` : '—'} testId="fact-stages" />
          <CompactFact
            label="Execution"
            value={execPercent === null || execPercent === undefined ? '—' : `${execPercent}%`}
            testId="fact-execution"
          />
          <CompactFact label={invoicedFact.label} value={invoicedFact.value} testId="fact-invoiced" />
        </div>
        {disclosure}
        {detailsBody}
      </header>
    );
  }

  return (
    <Panel as="header" className={cn('px-5 py-5 sm:px-6', className)} data-testid="project-header">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          <h1 className="min-w-0 font-heading text-[26px] font-black leading-[1.15] tracking-[-0.02em] text-ink [overflow-wrap:anywhere]">{project.name}</h1>
          <SystemChip systemType={project.system_type} className="h-6" />
          {project.status ? <StatusPill status={project.status} domain="project" /> : null}
          <HandoverPill handover={facts.handover} />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canViewHistory ? (
            <Button type="button" variant="ghost" size="icon" onClick={onOpenHistory} aria-label="History" title="History" data-testid="project-history">
              <History aria-hidden="true" />
            </Button>
          ) : null}
          {canEditDetails ? (
            <Button type="button" variant="outline" onClick={onEditDetails} data-testid="project-edit-details">
              <SquarePen aria-hidden="true" />
              Edit details
            </Button>
          ) : null}
          {menuItems.filter(Boolean).length ? (
            <ActionMenu
              label="Project actions"
              trigger={(
                <Button type="button" variant="outline" size="icon" aria-label="More project actions" data-testid="project-header-menu">
                  <Ellipsis aria-hidden="true" />
                </Button>
              )}
              items={menuItems}
            />
          ) : null}
        </div>
      </div>

      <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] text-ink-3">
        {project.client ? <InfoItem icon={Building2} title="Client">{project.client}</InfoItem> : null}
        {project.location ? <InfoItem icon={MapPin} title={project.location}>{project.location}</InfoItem> : null}
        {project.waze_url ? (
          <a
            href={project.waze_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-semibold text-[color:var(--brand-ink)] hover:underline"
          >
            <Navigation className="h-3.5 w-3.5" aria-hidden="true" />
            Open in Waze
            <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
          </a>
        ) : null}
        {project.contractor ? (
          <InfoItem icon={HardHat} title="Contractor">Contractor <b className="font-semibold text-ink-2">{project.contractor}</b></InfoItem>
        ) : null}
        {project.consultant ? (
          <InfoItem icon={UserRound} title="Consultant">Consultant <b className="font-semibold text-ink-2">{project.consultant}</b></InfoItem>
        ) : null}
        {scope.length ? (
          <span className="ml-auto flex flex-wrap gap-1.5" aria-label="Scope">
            {scope.map((item) => (
              <span key={item} className="inline-flex h-[22px] items-center rounded-md border border-line bg-surface-2 px-2 text-[12px] font-medium text-ink-2">{item}</span>
            ))}
          </span>
        ) : null}
      </div>

      <div className="mb-3.5 mt-4 h-px bg-line-2" aria-hidden="true" />
      <div ref={factsRef} className="min-w-0">
        <KeyFacts items={items} columns={factColumns} testId="project-key-facts" />
      </div>
      {disclosure}
      {detailsBody}
    </Panel>
  );
}

/**
 * "All project details" — every stored field, read-only; `deviceAddressControl` is the duplicate-address switch.
 * `canViewValue` (the Invoicing permission): the original scope value and the payment terms are money — without it
 * neither row is shown (owner rule 13 Oct 2026).
 */
export function ProjectDetailsList({ project, canViewValue, paymentTerms = [], onedriveLabel, deviceAddressControl, customFields = [] }) {
  const chips = (values) => (values.length ? (
    <span className="flex flex-wrap gap-1.5">
      {values.map((value) => (
        <span key={value} className="inline-flex h-[22px] items-center rounded-md border border-line bg-surface-2 px-2 text-[12px] font-medium text-ink-2">{value}</span>
      ))}
    </span>
  ) : null);
  const rows = [
    ['Client', project.client],
    ['Location', project.location],
    ['System type', project.system_type],
    ['Status', project.status],
    ['Start date', project.start_date ? formatDate(project.start_date) : ''],
    ['Handover date', project.end_date ? formatDate(project.end_date) : ''],
    ['Project Engineer', project.project_engineer],
    ['Senior Project Engineer', project.senior_project_engineer],
    ['Salesman', project.salesman],
    ['Consultant', project.consultant],
    ['Contractor / GC', project.contractor],
    canViewValue ? ['Original scope value', project.original_scope_value || project.total_project_value ? `AED ${formatNumber(project.original_scope_value ?? project.total_project_value, { decimals: 2 })}` : ''] : null,
    canViewValue ? ['Payment terms', chips(paymentTerms)] : null,
    ['Scope', chips(Array.isArray(project.scope) ? project.scope : [])],
    ['Tags', chips(Array.isArray(project.tags) ? project.tags : [])],
    ['OneDrive', onedriveLabel],
    ['OneDrive folder', project.files_root_path, 'break-all'],
    ...customFields.map((field) => [field.title || 'Field', field.content]),
    ['Notes', project.notes, 'whitespace-pre-wrap', 'wide'],
  ].filter(Boolean);
  return (
    <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
      <dl className="contents">
        {rows.map(([label, value, valueClass, span]) => (
          <div key={label} className={cn('min-w-0', span === 'wide' && 'sm:col-span-2 lg:col-span-4')}>
            <dt className="text-[11.5px] font-semibold text-ink-3">{label}</dt>
            <dd className={cn('mt-0.5 min-w-0 text-[13px] text-ink', valueClass)}>
              {value === null || value === undefined || value === '' ? <span className="text-ink-4" aria-label="Not set">—</span> : value}
            </dd>
          </div>
        ))}
      </dl>
      {deviceAddressControl ? (
        <div className="min-w-0 sm:col-span-2 lg:col-span-4">
          <div className="text-[11.5px] font-semibold text-ink-3">Device addresses</div>
          <div className="mt-1">{deviceAddressControl}</div>
        </div>
      ) : null}
    </div>
  );
}

/** The project tab row: counts, Files attention dot, save state at the right (pills on phones). */
export function ProjectTabsBar({ tabs, counts = {}, filesWarn, dirtyCount = 0, saving = false, saveError, phone = false, className }) {
  const list = tabs.map((tab) => ({
    ...tab,
    count: counts[tab.value],
    warn: tab.value === 'files' ? filesWarn || undefined : undefined,
  }));
  return (
    <TabsBar
      tabs={list}
      ariaLabel="Project sections"
      variant={phone ? 'pills' : 'underline'}
      // 11 #0: below 1400px only the ✓ shows for "All changes saved" (the words stay for screen readers), so the
      // tabs and their counts fit at 1280 / 1366; unsaved / saving / error keep their words
      status={(
        <SaveStatus
          dirtyCount={dirtyCount}
          saving={saving}
          error={saveError}
          savedLabel={<span className="max-[1399px]:sr-only" title="All changes saved">All changes saved</span>}
        />
      )}
      className={className}
      // relative: the tab list is the offsetParent of its tabs, so TabsBar's "keep the open tab in view"
      // measures from the list (not the page) and never scrolls the open tab's label out of view (tablets).
      listClassName="relative"
      testId="project-tabs"
    />
  );
}

/** Shimmer in the shape of a tab: toolbar line, then two cards. */
export function TabContentSkeleton({ label = 'Loading', rows = 6 }) {
  return (
    <div className="space-y-4" role="status" aria-busy="true" data-testid="tab-skeleton">
      <span className="sr-only">{label}</span>
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-2">
          <div className="ui-skeleton-shimmer h-5 w-32 rounded-md" />
          <div className="ui-skeleton-shimmer h-3.5 w-72 max-w-[60vw] rounded-md" />
        </div>
        <div className="ui-skeleton-shimmer h-9 w-36 rounded-control" />
      </div>
      <Panel className="space-y-3 p-5">
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="ui-skeleton-shimmer h-9 rounded-lg" style={{ opacity: 1 - index * 0.1 }} />
        ))}
      </Panel>
    </div>
  );
}

/** The whole page while the project itself loads (instead of a bare "Loading project..."). */
export function ProjectPageSkeleton() {
  return (
    <div className="space-y-5" role="status" aria-busy="true" data-testid="project-page-skeleton">
      <span className="sr-only">Loading project</span>
      <Panel className="space-y-4 px-6 py-5">
        <div className="ui-skeleton-shimmer h-7 w-72 max-w-full rounded-md" />
        <div className="ui-skeleton-shimmer h-4 w-[520px] max-w-full rounded-md" />
        <div className="grid grid-cols-2 gap-4 pt-2 lg:grid-cols-6">
          {Array.from({ length: 6 }, (_, index) => <div key={index} className="ui-skeleton-shimmer h-14 rounded-lg" />)}
        </div>
      </Panel>
      <div className="ui-skeleton-shimmer h-9 w-full rounded-md" />
      <TabContentSkeleton />
    </div>
  );
}
